import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1, 
    transition: { 
      staggerChildren: 0.3,
      delayChildren: 0.2
    } 
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { 
      duration: 0.8,
      ease: "easeOut"
    } 
  }
};

const imageVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { 
    opacity: 1, 
    scale: 1, 
    transition: { 
      duration: 0.6,
      ease: "easeOut"
    }
  }
};

const textVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { 
    opacity: 1, 
    x: 0, 
    transition: { 
      duration: 0.6,
      ease: "easeOut"
    }
  }
};

const cardData = [
  {
    title: "Create Your Sports Profile",
    description: "Build your personalized sports profile by sharing your athletic achievements, favorite sports, and past match experiences. Connect with other sports enthusiasts, showcase your skills, and stay active in your favorite sports community.",
    imageSrc: "images/sportProfile.svg",
    imageLeft: false
  },
  {
    title: "Set A Match.",
    description: "Easily organize and schedule matches with your friends or fellow athletes. Choose your sport, set the date and time, and invite participants to join. Keep track of upcoming games and enjoy a seamless way to arrange friendly competitions and tournaments.",
    imageSrc: "images/setMatch.svg",
    imageLeft: true
  },
  {
    title: "Track Games: Wins & Goals.",
    description: "Monitor your performance by tracking your wins, goals, and other stats for each game. Stay updated on your achievements, compare past results, and measure your progress as you aim for new milestones. Keep a detailed record of your sports journey and showcase your success.",
    imageSrc: "images/trackGames.svg",
    backgroundSrc: "images/trackGamesBG.svg",
    imageLeft: false
  },
  {
    title: "Amplify With Sport Buddies.",
    description: "Connect with fellow athletes and sports enthusiasts to boost your game. Build your network, share match highlights, and collaborate on team activities. Whether you're training or competing, having your sport buddies by your side will help elevate your performance and keep you motivated.",
    imageSrc: "images/sportBuddies.svg",
    imageLeft: true
  }
];

const Card = ({ title, description, imageSrc, iconSrc, backgroundSrc, imageLeft }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  return (
    <motion.section 
      ref={ref}
      className="relative z-10 flex flex-col items-center justify-center mt-10 w-full max-w-[1843px] mx-auto text-neutral-700"
      variants={cardVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
    >
      <div className={`flex gap-5 w-full max-md:flex-col ${imageLeft ? 'flex-row-reverse' : ''}`}>
        <motion.div 
          className="flex flex-col w-3/5 max-md:w-full relative"
          variants={imageVariants}
        >
          {backgroundSrc && (
            <img
              className="w-full h-auto"
              loading="lazy"
              src={backgroundSrc}
              alt="Background"
            />
          )}
          <motion.img
            className={backgroundSrc ? "absolute center w-[100%] h-[98%] max-w-full max-h-full" : "w-full h-auto"}
            loading="lazy"
            src={imageSrc}
            alt={title}
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.3 }}
          />
        </motion.div>
        <motion.div 
          className="flex flex-col justify-center w-2/5 max-md:w-full ml-4 relative"
          variants={textVariants}
        >
          <div className="mt-10 max-md:mt-10">
            <h2 className="text-3xl font-medium leading-none max-md:text-4xl max-md:text-center text-neutral-700">
              {title}
            </h2>
            <p className="mt-10 text-xl font-light leading-relaxed text-black max-md:text-center max-md:mt-6">
              {description}
            </p>
          </div>
          {iconSrc && (
            <motion.img
              src={iconSrc}
              alt="Icon"
              className="absolute top-12 left-64 w-24 h-24 object-contain"
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.8 }}
            />
          )}
        </motion.div>
      </div>
    </motion.section>
  );
};

function ConsolidatedCards() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  return (
    <motion.div 
      ref={ref}
      className="flex overflow-hidden flex-col bg-gray-100"
      variants={containerVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
    >
      <div className="flex flex-col pr-7 pl-16 w-full max-md:px-5 max-md:max-w-full">
      {cardData.map((card, index) => (
          <Card key={index} {...card} />
        ))}
      </div>
    </motion.div>
  );
}

export default ConsolidatedCards;